import { Injectable, signal } from '@angular/core';
import { AUTH_STORAGE_KEYS } from '../auth/models/auth.models';
import type { AdminUser } from '../interfaces/admin-auth.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  // All session data is stored purely in application memory (no localStorage or sessionStorage)
  private accessTokenSignal = signal<string | null>(null);
  private walletAddressSignal = signal<string | null>(null);
  private userSignal = signal<AdminUser | null>(null);

  storeSession(payload: {
    accessToken?: string;
    refreshToken?: string;
    walletAddress?: string;
    user?: AdminUser | null;
  }): void {
    if (payload.accessToken) {
      this.setAccessToken(payload.accessToken);
    }

    // Refresh token is not stored on the client side at all as it is handled by HttpOnly cookies.
    // If passed to payload, we do not write it.

    if (payload.walletAddress) {
      this.walletAddressSignal.set(payload.walletAddress);
    }

    if (payload.user) {
      this.userSignal.set(payload.user);
    }
  }

  clearSession(): void {
    this.setAccessToken(null);
    this.walletAddressSignal.set(null);
    this.userSignal.set(null);
  }

  hasStoredSession(): boolean {
    // Session exists in memory if we have an access token, wallet address, or user profile in memory
    return Boolean(
      this.getAccessToken() ||
        this.getStoredWalletAddress() ||
        this.getStoredUser()
    );
  }

  getAccessToken(): string | null {
    return this.accessTokenSignal();
  }

  setAccessToken(token: string | null): void {
    this.accessTokenSignal.set(token);
  }

  getStoredWalletAddress(): string | null {
    return this.walletAddressSignal();
  }

  getStoredUser(): AdminUser | null {
    return this.userSignal();
  }
}
